import { useState, useEffect } from "react";
import { Typography, Box, Grid, Paper, CircularProgress } from "@mui/material";
import { onAuthStateChanged } from "firebase/auth";
import thanq from "../api/thanq";
import { auth } from "../utils/firebase";
import { useSnackbar } from "notistack";

const Metric = ({ title, value }) => {
    return (
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
                {title}
            </Typography>
            <Box>
                <center>
                    <Typography variant="body1">{value}</Typography>
                </center>
            </Box>
        </Paper>
    );
};

const MetricGroup = ({ title, children }) => {
    // capitalizes the first letter of each word
    const updatedTitle = title.charAt(0).toUpperCase() + title.slice(1);

    return (
        <Grid container xs={12} md={6} direction={"column"} sx={{ pl: 3, pt: 2 }}>
            <Typography variant="h6" gutterBottom>
                {updatedTitle}
            </Typography>
            <Grid container xs={12} md={6} direction={"row"} spacing={3}>
                {children}
            </Grid>
        </Grid>
    );
};

const Dashboard = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [authLoading, setAuthLoading] = useState(true);
    const [user, setUser] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await thanq.get("/admin/metrics");
            setData(response.data);
        } catch (err) {
            enqueueSnackbar(err.message, { variant: "error", autoHideDuration: null });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Listen for auth state changes
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            setAuthLoading(false);

            if (!currentUser) {
                enqueueSnackbar("Please log in to view dashboard", { variant: "warning" });
            }
        });

        return () => unsubscribe();
    }, [enqueueSnackbar]);

    useEffect(() => {
        // Only fetch data when user is authenticated and auth is not loading
        if (!authLoading && user) {
            fetchData();
        }
        // eslint-disable-next-line
    }, [user, authLoading]);

    if (loading) return <CircularProgress />;
    if (!data) return <Typography>No data available</Typography>;

    const { users, tips } = data;
    const { total } = tips;

    return !data ? (
        <Typography variant="h4" gutterBottom>
            No data to display
        </Typography>
    ) : (
        <Grid container spacing={3} direction={"column"}>
            <Grid item xs={12}>
                <Typography variant="h4" gutterBottom>
                    Dashboard
                </Typography>
            </Grid>
            <Grid container>
                <MetricGroup title="Users">
                    <Metric title="Total Users" value={users.total} />
                    <Metric title="Onboarded Users" value={users.onboarded} />
                </MetricGroup>
                <MetricGroup title="Tips">
                    <Metric title="Total" value={total?.amount} />
                    <Metric title="Average" value={total?.average} />
                    <Metric title="Count" value={total?.count} />
                </MetricGroup>
            </Grid>
            {Object.entries(tips).map(([parentKey, parentValue]) => {
                if (parentKey === "total") return <></>;
                return (
                    <MetricGroup title={`${parentKey} Details`} key={parentKey}>
                        {Object.entries(parentValue).map(([childKey, childValue]) => {
                            return (
                                <MetricGroup title={childKey} key={childKey}>
                                    <Metric title="Total" value={childValue?.amount} />
                                    <Metric title="Average" value={childValue?.average} />
                                    <Metric title="Count" value={childValue?.count} />
                                </MetricGroup>
                            );
                        })}
                    </MetricGroup>
                );
            })}
        </Grid>
    );
};

export default Dashboard;
